import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { Observable, map, startWith, take } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { FORTUNE_1000_COMPANIES } from './fortune-1000-companies';
import { WORK_TYPE_CONFIG } from '@app/utils/work-type-config';
import { analytics } from '@app/events/segment/analytics';

export type RoleType = 'full_time' | 'part_time' | 'internship' | 'apprenticeship';
export type JobSource = 'Compass' | 'Handshake' | 'LinkedIn' | 'Networking or referral' | 'Other';

export interface JobLandingDialogResult {
  roleType: RoleType;
  organization: string;
  source?: string;
}

export interface JobLandingDialogData {
  entrypoint: string;
  section: string;
}

const JOB_LANDING_LOCAL_KEY = 'compass_job_landing_data';

const SOURCE_TO_WORK_REFERRER: Record<JobSource, string> = {
  'Compass': 'compass',
  'Handshake': 'handshake',
  'LinkedIn': 'linked_in',
  'Networking or referral': 'networking_or_referral',
  'Other': 'other',
};

@Component({
  selector: 'app-job-landing-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatFormFieldModule,
    MatRadioModule,
    AsyncPipe,
  ],
  templateUrl: './job-landing-dialog.component.html',
  styleUrl: './job-landing-dialog.component.css',
})
export class JobLandingDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<JobLandingDialogComponent>);

  selectedRole: RoleType | null = null;
  organizationControl = new FormControl('');
  filteredOrganizations!: Observable<string[]>;
  selectedSource: JobSource | null = null;
  otherSourceText = '';
  private submitted = false;

  roleCards = WORK_TYPE_CONFIG.map((c) => ({
    type: c.key as RoleType,
    label: c.label,
    icon: c.icon,
    description: c.description,
  }));

  sourceOptions: JobSource[] = [
    'Compass',
    'Handshake',
    'LinkedIn',
    'Networking or referral',
    'Other',
  ];

  constructor(@Inject(MAT_DIALOG_DATA) private data: JobLandingDialogData) {
    this.filteredOrganizations = this.organizationControl.valueChanges.pipe(
      startWith(''),
      map((value) => (value ? this.filterCompanies(value) : []))
    );
  }

  ngOnInit(): void {
    analytics.track('Job Landing Modal Open', {
      entrypoint: this.data?.entrypoint ?? '',
      section: this.data?.section ?? '',
    });

    this.dialogRef.beforeClosed().pipe(take(1)).subscribe(() => {
      if (!this.submitted) {
        analytics.track('Job Landing Modal Abandon', {
          entrypoint: this.data?.entrypoint ?? '',
          section: this.data?.section ?? '',
        });
      }
    });
  }

  private filterCompanies(query: string): string[] {
    const lower = query.toLowerCase();
    const terms = lower.split(/\s+/).filter(Boolean);

    return FORTUNE_1000_COMPANIES.filter((company) => {
      const companyLower = company.toLowerCase();
      return terms.every((term) => companyLower.includes(term));
    }).slice(0, 20);
  }

  selectRole(type: RoleType): void {
    this.selectedRole = type;
  }

  get isSubmitDisabled(): boolean {
    return !this.selectedRole || !this.organizationControl.value?.trim();
  }

  onSubmit(): void {
    if (this.isSubmitDisabled) return;

    const result: JobLandingDialogResult = {
      roleType: this.selectedRole!,
      organization: this.organizationControl.value!.trim(),
      source: this.selectedSource === 'Other'
        ? (this.otherSourceText.trim() || 'Other')
        : (this.selectedSource ?? undefined),
    };

    const payload: {
      work_type: string;
      employer_name: string;
      work_referrer?: string;
      work_referrer_other?: string;
    } = {
      work_type: result.roleType,
      employer_name: result.organization,
    };

    if (result.source) {
      const mappedSource = SOURCE_TO_WORK_REFERRER[result.source as JobSource];
      if (mappedSource && mappedSource !== 'other') {
        payload.work_referrer = mappedSource;
      } else {
        payload.work_referrer = 'other';
        payload.work_referrer_other = result.source;
      }
    }

    analytics.track('Job Landing Modal Submit', {
      ...payload,
      entrypoint: this.data?.entrypoint ?? '',
      section: this.data?.section ?? '',
    });

    localStorage.setItem(JOB_LANDING_LOCAL_KEY, JSON.stringify(result));
    this.submitted = true;
    this.dialogRef.close(result);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
